<script setup lang="ts">
import { ref, watch, watchEffect } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { useVehicleStore } from '@/stores/vehicleStore'
import { type Vehicle } from '@/types/Vehicle'
import VehicleFormDialog from '@/components/vehicle/VehicleFormDialog.vue'

const modelValue = defineModel<boolean>()
const emit = defineEmits<{
  (e: 'select', vehicle: Vehicle): void
}>()

const props = defineProps<{
  searchPrefill?: string
}>()

const tagQuery = ref('')
const results = ref<Vehicle[]>([])
const searching = ref(false)
const hasSearched = ref(false)
const vehicleStore = useVehicleStore()

const createDialogVisible = ref(false)
const newVehicle = ref<Vehicle | null>(null)

watch(modelValue, async (val) => {
  if (val) {
    tagQuery.value = props.searchPrefill || ''
    results.value = []
    hasSearched.value = false
    if (tagQuery.value.trim()) await search()
  }
})

async function search() {
  if (!tagQuery.value.trim()) return
  hasSearched.value = true
  searching.value = true
  results.value = await vehicleStore.findOrFetchByTag(tagQuery.value.trim())
  searching.value = false
}

function createNew() {
  newVehicle.value = {
    tagNumber: tagQuery.value.trim(),
    state: '',
    createdAt: new Date(),
    updatedAt: new Date(),
  }
  createDialogVisible.value = true
}

function onSave(vehicle: Vehicle) {
  emit('select', vehicle)
  modelValue.value = false
}

function choose(vehicle: Vehicle) {
  emit('select', vehicle)
  modelValue.value = false
}
</script>

<template>
  <Dialog v-model:visible="modelValue" header="Select Vehicle by Tag" modal class="w-[500px]">
    <div class="space-y-4">
      <div class="flex gap-2 items-center">
        <InputText
          v-model="tagQuery"
          placeholder="Enter tag number..."
          class="flex-1"
          @keydown.enter="search"
        />
        <Button label="Search" icon="pi pi-search" @click="search" :loading="searching" />
      </div>

      <div v-if="results.length > 0" class="space-y-2">
        <div
          v-for="v in results"
          :key="v.id"
          class="border p-2 rounded flex justify-between items-center"
        >
          <div>
            <div class="font-medium">
              {{ v.tagNumber }} <span v-if="v.state">({{ v.state }})</span>
            </div>
            <div class="text-sm text-gray-500">
              {{ v.year || '—' }} {{ v.make || '' }} {{ v.model || '' }} {{ v.color || '' }}
            </div>
          </div>
          <Button label="Select" @click="choose(v)" />
        </div>
      </div>

      <div v-else-if="hasSearched && tagQuery && !searching" class="text-center text-gray-500">
        No matches found.
        <div class="mt-2">
          <Button label="Create New Vehicle" @click="createNew" />
        </div>
      </div>
    </div>
  </Dialog>

  <VehicleFormDialog
    v-if="newVehicle"
    v-model="createDialogVisible"
    :vehicle="newVehicle"
    @save="onSave"
  />
</template>
